import { computed, reactive, readonly } from 'vue'
import {
  type ExternalFeatureId,
  type LocalFeatureId,
  localFeatureIds,
  isLocalFeature,
  isExternalFeature,
} from './types'
import { useFeatureFlag } from '/@/core/experiment'

interface FeatureLaunch {
  launchDate: Date
}
const featureLaunches: Record<string, FeatureLaunch> = {
  budget: { launchDate: new Date('2022-07-12') },
  'manual-eraser': { launchDate: new Date('2022-08-08') },
  'ruler-feature': { launchDate: new Date('2022-07-09') },
  'viz-3': { launchDate: new Date('2023-03-01') },
  'magic-install': { launchDate: new Date('2023-04-24') },
  'smart-add': { launchDate: new Date('2023-05-15') },
}

export function featureLaunch(name: string): FeatureLaunch | undefined {
  return featureLaunches[name]
}

const state = reactive<Record<LocalFeatureId, boolean>>({
  'export video': false,
  'ss onboarding': true,
  'smartlook-canvas': false,
  'onboarding-pro-schedule': false,
  'sourcing-assistant': true,
  'design-assistant': false,
  'signup-tracking-page': false,
  'price-alert': true,
  slazzer: true,
  'viz-magic-create-background': false,
  'viz-magic-create-object': true,
  'viz-demo-overlay': false,
  'viz-take-photo-button': false,
  'search-with-image-embedding': false,
  'viz-manual-distort': false,
  'viz-magic-render-floorplan': false,
})

const sessionStorageKey = '__spoak-feature-map'

// rehydrate from sessionStorage
try {
  const cache = (JSON.parse(
    sessionStorage.getItem(sessionStorageKey) ?? 'null',
  ) ?? []) as Array<[id: LocalFeatureId, enabled: boolean]>
  for (const [id, bool] of cache) {
    if (!localFeatureIds.includes(id)) continue
    state[id] = bool
  }
} catch {
  // whatever
}

export function getLocalFeatureState() {
  return readonly(state)
}

export function useFeature(featureId: LocalFeatureId | ExternalFeatureId) {
  const localEnabled = computed(
    () => isLocalFeature(featureId) && state[featureId],
  )

  const shouldFetchExternalFlag = computed(() => {
    return !localEnabled.value
  })

  const { featureFlag, loading: isLoadingFeatureFlag } = useFeatureFlag(
    featureId,
    true,
    shouldFetchExternalFlag,
  )

  const externalEnabled = computed(() => {
    if (!isExternalFeature(featureId)) return false
    return featureFlag.value === 'on' || featureFlag.value === 'treatment'
  })

  const loading = computed(() => isLoadingFeatureFlag.value)

  const enabled = computed(() => {
    const bool = (() => {
      if (localEnabled.value) {
        return true
      } else {
        return externalEnabled.value
      }
    })()
    return bool
  })

  return reactive({
    enabled,
    loading,
  })
}

export function __useFeaturesApi() {
  return reactive({
    featureTuples: computed(
      () =>
        Object.entries(state) as Array<[id: LocalFeatureId, enabled: boolean]>,
    ),
    set(id: LocalFeatureId, bool: boolean) {
      state[id] = bool
      sessionStorage.setItem(
        sessionStorageKey,
        JSON.stringify(Object.entries(state)),
      )
    },
  })
}
